<script setup lang="ts">
import {IconBlock, IconEarning, IconReport, IconRestrictUser, IconRestrictViewUser} from "~/components/ui/icons";
import type {Profile} from "~/repository/types";
import {PostScope} from "~/repository/types.d";
import {useToast} from "~/components/ui/toast";
import {StaticModal} from "~/components/ui/modal";
import NewModal from "~/components/NewModal.vue";

await preloadComponents(['ProfilePostSkeleton', 'UserAvatar', 'ProfileCompletionSteps'])

const {username} = useRouteParams<{
  username: string
}>()

const {toast} = useToast();
const {t} = useI18n();

const {fetch: fetchProfile, usePurchasedPostsFetch} = useProfileAPI();
const {data: profile} = await fetchProfile<Profile>(username);

useHead({
  title: `${profile.value.name} (@${profile.value.username})`
})

const activeTab = ref('posts');

const {
  fetching,
  loadMoreElement,
  canGoNextPage,
  forgetPost,
  posts,
} = useProfileFeedAPI(profile.value);

const {unfollow} = useUserAPI(profile)
const router = useRouter()
const {APP_URL} = useRuntimeConfig().public;
const openedEditModal = ref(false);
const {
  fetching: fetchingPurchasedPosts,
  fetch: fetchPurchasedPosts,
  posts: purchasedPosts
} = usePurchasedPostsFetch();


const {formatNumber} = useFormatNumber();
const {isLoggedIn} = useAuthStore();

const formattedLikes = computed(() => formatNumber(profile.value!.stats.likes));
const formattedFollowers = computed(() => formatNumber(profile.value!.stats.followers));


function closeEditModal() {
  openedEditModal.value = false;
}

onMounted(async () => {
  await fetchPurchasedPosts();
});

const dialogOpen = ref(false);

function handleOpen() {
  dialogOpen.value = true;
}

// @todo fetch subscribers only posts from API not from already fetched posts
const subscriberPosts = computed(() => {
  const postArray = Object.values(posts.value);
  return postArray.filter(post => post.scope === PostScope.SUBSCRIBERS_ONLY);
});

const copyLink = () => {
  const url = `${APP_URL}${username}`;
  navigator.clipboard.writeText(url).then(() => {
    toast({
      title: t('toast.copy_link'),
      duration: 1500,
    });
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};


watch(dialogOpen, (newVal) => {
  if (!newVal) {
    setTimeout(() => {
      document.body.removeAttribute('style');
    }, 200);
  }
});

const alertOpen = ref(false);
const confirmUnfollowOpen = ref(false);
const reportDialogOpen = ref(false);

function handleOpenAlert() {
  alertOpen.value = true;
}

function handleConfirmUnfollowOpen() {
  confirmUnfollowOpen.value = true;
}

function handleOpenDialog() {
  reportDialogOpen.value = true;
}

async function refreshProfile() {
  const {data} = await fetchProfile(username);
  if (data) {
    profile.value = data.value
  }
}

watch(alertOpen, (newVal) => {
  if (!newVal) {
    setTimeout(() => {
      document.body.removeAttribute('style');
    }, 200);
  }
});

watch(reportDialogOpen, (newVal) => {
  if (!newVal) {
    setTimeout(() => {
      document.body.removeAttribute('style');
    }, 200);
  }
});

const {init: initPayment} = usePaymentStore()

const selectedPlan = ref('month');
const success_url = router.resolve({name: 'username-subscribe-processing', params: {username: profile.value.username}})

async function initSubscription() {
  await initPayment(`subscribe/${profile.value.username}`, {
    interval: selectedPlan.value,
    return_url: `${APP_URL}${success_url.fullPath.substring(1)}`
  }, new Subscribe(
    profile.value
  ))
}

onUnmounted(() => {
  document.title = "Redlike";
});


async function restrictUser(reason: 'view-restrict' | 'restrict' | 'block') {
  const userId = profile.value?.id;
  if (!userId) {
    toast({title: t('toast.user_not_found'), variant: "destructive"});
    return;
  }

  try {
    const data = await $larafetch(`restrict/${userId}`, {
      method: "POST",
      body: {reason},
    });

    if (data.status) {
      if (reason === 'block') {
        alertOpen.value = false;
        await router.push({name: 'index'});
        toast({title: t('toast.block_success'), duration: 1500});
      } else {
        toast({title: t('toast.report_sent'), duration: 1500});
      }
    } else {
      toast({title: t('toast.report_failed'), duration: 1500});
    }
  } catch (error: any) {
    if (error?.status === 403 && reason === 'block') {
      toast({
        title: t('toast.already_blocked'),
        description: t('toast.already_blocked_description'),
        duration: 2000,
      });
      alertOpen.value = false;
      return;
    }

    console.error(t('toast.error'), error);
    toast({
      title: t('toast.error'),
      description: t('toast.restrict_error'),
      duration: 1500,

    });
  }
}


</script>

<template>
  <section class="max-w-2xl px-3 md:px-6 mx-auto relative">

    <SubscriptionBanner
      :postsNumber="Object.keys(subscriberPosts).length"
      v-if="isLoggedIn && !profile.is_owner && profile.is_followed &&  (profile.is_creator && !profile.subscription.is_subscribed)"
      :profile="profile" @subscribe="initSubscription"/>
    <div class="py-4">
      <div class="grid grid-cols-[minmax(0,1fr)_max-content] gap-x-4">
        <div class="flex flex-col justify-center">
          <h2 class="whitespace-pre-line break-words text-2xl font-bold" style="word-break: break-word">{{
              profile?.name
            }}</h2>
          <div><span class="whitespace-pre-line break-words select-none"
                     style="word-break: break-word">{{ profile?.username }}</span></div>
        </div>
        <Dialog>
          <DialogTrigger>
            <UserAvatar :user="profile" size="lg"/>
          </DialogTrigger>
          <DialogContent overlay="blurred" class="border-0 bg-transparent min-h-svh h-full" content="transparent">
            <DialogClose class="absolute top-4 right-4">
              <Button class="bg-white rounded-full p-3 h-10 w-10">
                <IconClose color="#000000" size="18px"/>
              </Button>
            </DialogClose>
            <UserAvatar :user="profile" size="xxl" class="m-auto"/>
            <div class="flex gap-7 fixed bottom-8 left-1/2 -translate-x-1/2">
              <div class="flex flex-col items-center gap-1">
                <Button class="bg-white w-auto h-auto aspect-square rounded-full  p-4">
                  <span><IconShare color="#000000"/></span>
                </Button>
                <div class="text-white">
                  {{ t('dropdown.share') }}
                </div>
              </div>

              <div class="flex flex-col items-center gap-1">
                <Button @click="copyLink" class="bg-white rounded-full w-auto h-auto aspect-square p-4">
                  <span><IconLink color="#000000"/></span>
                </Button>
                <div class="text-white">
                  {{ t('dropdown.copy_link') }}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div class="mt-4 whitespace-break-spaces" style="word-break: break-word">
        {{ profile?.bio }}
      </div>
      <div>
      </div>

      <div class="mt-1.5 flex items-center">
        <StaticModal :height="75" ref="modalRef">
          <template #trigger="{ open }">
            <Button class="w-auto px-0 shrink-1" @click="open" variant="muted">
              <span class="font-bold text-foreground mr-1">{{ formattedFollowers }}</span>
              {{ t('buttons.followers') }}
            </Button>
          </template>
          <template #dialog-content>
            <DialogContent class="max-w-lg">
              <LazyModalsFollowersModal :user="profile"/>
              <NuxtErrorBoundary>
                <template #error="{ error }">
                  <LazyModalsLoginModal/>
                </template>
              </NuxtErrorBoundary>
            </DialogContent>
          </template>
          <template #static-drawer-content>
            <LazyModalsFollowersModal :user="profile"/>
            <NuxtErrorBoundary>
              <template #error="{ error }">
                <LazyModalsLoginModal/>
              </template>
            </NuxtErrorBoundary>
          </template>
        </StaticModal>
        <span class="px-2 text-gray">·</span>
        <div class="flex grow text-gray text-ellipsis overflow-hidden font-normal">
          <span class="font-bold text-foreground mr-1">{{
              formattedLikes
            }}</span>{{ t('post.counters.likes_without_counters') }}
        </div>
        <div class="flex ml-4" v-auto-animate v-if="isLoggedIn">
          <Button as="a" :href="socialAccount.href" target="_blank" variant="icon" size="icon" class="mr-4"
                  v-for="socialAccount in profile?.social_accounts">
            <IconInstagram v-if="socialAccount.provider === 'instagram'" size="24px"/>
          </Button>
          <DropdownDrawer v-if="!profile.is_owner" :useRectangleIcon="true">
            <template #content-drawer>
              <DrawerDescription class="m-4 rounded-xl bg-neutral-100">
                <DrawerClose class="w-full">
                  <DrawerMenuItem @click="handleOpen">{{ t('dropdown.information') }}
                    <ClientOnly>
                      <IconInfo size="20px"/>
                    </ClientOnly>
                  </DrawerMenuItem>
                </DrawerClose>
                <DrawerMenuSeparator/>

                <template v-if="!profile.subscription.is_subscribed && profile.is_followed">
                  <DrawerClose class="w-full">
                    <DrawerMenuItem @click="handleConfirmUnfollowOpen">{{ t('dropdown.stop_following') }}
                    </DrawerMenuItem>
                  </DrawerClose>
                  <DrawerMenuSeparator/>
                </template>

                <DrawerClose class="w-full">
                  <DrawerMenuItem @click="restrictUser('view-restrict')">
                    {{ t('dropdown.hide') }}
                    <ClientOnly>
                      <IconRestrictViewUser size="20px"/>
                    </ClientOnly>
                  </DrawerMenuItem>
                </DrawerClose>
                <DrawerMenuSeparator/>

                <DrawerClose class="w-full">
                  <DrawerMenuItem @click="restrictUser('restrict')">
                    {{ t('dropdown.restrict') }}
                    <ClientOnly>
                      <IconRestrictUser size="20px"/>
                    </ClientOnly>
                  </DrawerMenuItem>
                </DrawerClose>
                <DrawerMenuSeparator/>

                <DrawerClose class="w-full select-none text-red font-semibold">
                  <DrawerMenuItem class="text-red font-semibold" @click="handleOpenAlert">
                    {{ t('dropdown.block') }}
                    <ClientOnly>
                      <IconBlock size="20px"/>
                    </ClientOnly>
                  </DrawerMenuItem>
                </DrawerClose>
                <DrawerMenuSeparator/>

                <DrawerClose class="w-full select-none">
                  <DrawerMenuItem class="text-red font-semibold" @click="handleOpenDialog">
                    {{ t('dropdown.report') }}
                    <ClientOnly>
                      <IconReport size="20px"/>
                    </ClientOnly>
                  </DrawerMenuItem>
                </DrawerClose>
              </DrawerDescription>
            </template>
            <template #content-dropdown>
              <DropdownMenuItem @click="handleOpen" class="flex justify-between">{{ $t('dropdown.information') }}
                <ClientOnly>
                  <IconInfo size="20px"/>
                </ClientOnly>
              </DropdownMenuItem>
              <template v-if="!profile.subscription.is_subscribed && profile.is_followed">
                <DropdownMenuItem @click="handleConfirmUnfollowOpen" class="flex justify-between">
                  {{ t('dropdown.stop_following') }}
                </DropdownMenuItem>
              </template>
              <DropdownMenuItem class="flex justify-between" @click="restrictUser('view-restrict')">
                {{ t('dropdown.hide') }}
                <IconRestrictViewUser size="20px"/>
              </DropdownMenuItem>

              <DropdownMenuItem class="flex justify-between" @click="restrictUser('restrict')">
                {{ t('dropdown.restrict') }}
                <IconRestrictUser size="20px"/>
              </DropdownMenuItem>

              <DropdownMenuItem class="flex justify-between text-red" @click="handleOpenAlert">
                {{ t('dropdown.block') }}
                <IconBlock size="20px"/>
              </DropdownMenuItem>
              <DropdownMenuItem class="flex justify-between text-red" @click="handleOpenDialog">
                {{ t('dropdown.report') }}
                <IconReport color="#ff1856" size="20px"/>
              </DropdownMenuItem>
            </template>
          </DropdownDrawer>
          <Dialog v-model:open="reportDialogOpen">
            <DialogContent class="max-md:max-w-[90%]">
              <LazyModalsReportModal @close="reportDialogOpen = false"/>
            </DialogContent>
          </Dialog>
          <Dialog v-model:open="dialogOpen">
            <DialogContent class="max-md:max-w-[90%]">
              <LazyModalsProfileInfoModal :user="profile"/>
            </DialogContent>
          </Dialog>
          <AlertDialog v-model:open="confirmUnfollowOpen">
            <AlertDialogContent class="p-0 gap-0 overflow-hidden">
              <AlertDialogHeader class="flex items-center gap-y-4 p-6">
                <UserAvatar :user="profile" size="lg"/>
                <AlertDialogTitle class="text-base">
                  {{ t('alert.unfollow_title', {username: profile?.username}) }}
                </AlertDialogTitle>
                <!--                <AlertDialogDescription>-->
                <!--                  {{ $t('alert.block_description', {name: profile?.name}) }}-->
                <!--                </AlertDialogDescription>-->
              </AlertDialogHeader>
              <AlertDialogFooter class="border-t-[0.5px] border-gray-400 h-16 grid grid-cols-2">
                <AlertDialogCancel class="bg-background h-full text-gray border-0 mt-0"> {{ t('buttons.Cancel') }}
                </AlertDialogCancel>
                <AlertDialogAction
                  class="bg-background h-full border-l-[0.5px] rounded-none border-gray-400 text-red font-bold whitespace-normal"
                  @click="unfollow">
                  {{ t('alert.unfollow_confirm') }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog v-model:open="alertOpen">
            <AlertDialogContent class="p-0 gap-0 overflow-hidden">
              <AlertDialogHeader class="p-6">
                <AlertDialogTitle>
                  {{ t('alert.block_user', {name: profile?.username}) }}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {{ t('alert.block_description', {name: profile?.username}) }}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter class="border-t-[0.5px] border-gray-400 h-16  grid grid-cols-2">
                <AlertDialogCancel class="bg-background h-full !mt-0 text-gray border-0"> {{ t('buttons.Cancel') }}
                </AlertDialogCancel>
                <AlertDialogAction
                  class="bg-background h-full border-l-[0.5px] rounded-none border-gray-400 text-red font-bold whitespace-normal"
                  @click="restrictUser('block')">
                  {{ t('dropdown.block') }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

    </div>
    <div class="py-3 flex gap-x-2.5">
      <template v-if="!profile.is_owner">
        <template v-if="profile.is_followed">
          <UserFollowButton v-if="!profile.is_creator" :profile="profile"/>
          <Button class="bg-gradient-button text-white" v-if="profile.subscription.is_subscribed">
            {{ t('buttons.subscribed') }}
          </Button>
          <Button class="bg-gradient-button text-white" variant="default" @click="initSubscription" v-else>
            {{ t('buttons.subscribe') }}
          </Button>
        </template>
        <NuxtErrorBoundary v-else>
          <UserFollowButton :profile="profile"/>
          <template #error="{clearError}">
            <UserFollowButton :profile="profile"/>
            <NewModal :model-value="true" @update:model-value="clearError">
              <LazyModalsLoginModal/>
            </NewModal>
          </template>
        </NuxtErrorBoundary>
        <NuxtErrorBoundary>
          <UserSendMessageButton :profile="profile"/>
          <template #error="{clearError}">
            <Button variant="outline">{{ t('buttons.send_message') }}</Button>
            <NewModal :model-value="true" @update:model-value="clearError">
              <LazyModalsLoginModal/>
            </NewModal>
          </template>
        </NuxtErrorBoundary>
      </template>
      <Modal v-model="openedEditModal" v-else>
        <template #trigger="{ open }">
          <Button variant="outline" @click="open">{{ t('buttons.edit_profile') }}</Button>
        </template>
        <template #dialog-content>
          <DialogContent>
            <DialogHeader>
              <button @click="openedEditModal = false" class="w-auto text-left font-normal text-[17px]"
                      variant="ghost">
                {{ t('buttons.Cancel') }}
              </button>
              <h2 class="text-center"> {{ t('buttons.edit_profile') }}</h2>
            </DialogHeader>
            <NuxtLazyHydrate when-visible>
              <LazyModalsEditProfileModal v-model="profile" :profile="profile" @close="closeEditModal"/>
            </NuxtLazyHydrate>
          </DialogContent>
        </template>
        <template #drawer-content>
          <DrawerContent class="min-h-auto h-auto max-h-dvh">
            <DrawerHeader>{{ t('buttons.edit_profile') }}</DrawerHeader>
            <NuxtLazyHydrate when-visible>
              <LazyModalsEditProfileModal v-model="profile" :profile="profile" @close="closeEditModal"/>
            </NuxtLazyHydrate>
          </DrawerContent>
        </template>
      </Modal>
      <Button v-if="profile.is_owner" :to="{name: 'creator-panel'}" @click="sendMessage">
        {{ t('buttons.creator_panel') }}
      </Button>
    </div>
  </section>

  <ProfileCompletionSteps :profile="profile" :hasPosts="profile.stats.posts > 0"/>

  <section class="max-w-2xl md:px-6 mx-auto py-4">

    <Tabs v-model="activeTab">
      <TabsList class="bg-background z-30 h-12">
        <TabsTrigger value="posts"
                     class="flex flex-col py-0 h-12 data-[state=active]:border-t data-[state=active]:border-b-0 !border-b-0 border-gray-50 border-t">
          <span class="font-semibold"><IconAddPreview/></span>
        </TabsTrigger>
        <TabsTrigger value="supervision"
                     class="flex flex-col py-0 h-12 data-[state=active]:border-t data-[state=active]:border-b-0 !border-b-0 border-gray-50 border-t">
          <span class="font-semibold"><IconCrown/></span>
        </TabsTrigger>
        <TabsTrigger v-if="profile.is_owner && Object.keys(purchasedPosts).length > 0" value="purchased"
                     class="flex flex-col py-0 h-12 data-[state=active]:border-t data-[state=active]:border-b-0 !border-b-0 border-gray-50 border-t">
                  <span class="font-semibold"><svg aria-label="" class="x1lliihq x1n2onr6 x5n08af" fill="currentColor"
                                                   height="24" role="img" viewBox="0 0 24 24" width="24"><title></title><path
                    d="M12.001.504a11.5 11.5 0 1 0 11.5 11.5 11.513 11.513 0 0 0-11.5-11.5Zm0 21a9.5 9.5 0 1 1 9.5-9.5 9.51 9.51 0 0 1-9.5 9.5Zm4.691-11.82L13.91 9.35l-1.08-2.537a.893.893 0 0 0-1.66 0L10.086 9.35l-2.783.334a.963.963 0 0 0-.493 1.662l2.095 1.905-.606 2.837a.918.918 0 0 0 1.363 1.018l2.335-1.504 2.335 1.504a.918.918 0 0 0 1.363-1.018l-.605-2.837 2.094-1.905a.962.962 0 0 0-.493-1.662Z"
                    fill-rule="evenodd"></path></svg></span>
        </TabsTrigger>
      </TabsList>
      <TabsContent value="posts" :forceMount="true" v-if="activeTab === 'posts'" class="">

        <NuxtErrorBoundary>
          <PostList :posts="posts" :fetching="fetching" @delete:post="forgetPost"/>
          <template #error="{ clearError, error }">
            <PostList :posts="posts"/>
            <NewModal :model-value="true" @update:model-value="clearError">
              <LazyModalsLoginModal/>
            </NewModal>
          </template>
        </NuxtErrorBoundary>

        <div ref="loadMoreElement" v-if="canGoNextPage"/>

        <div v-if="!fetching && Object.keys(posts).length < 1"
             class="flex flex-col flex-grow items-center justify-center mt-5">
          <p v-if="!profile.is_owner" class="text-gray text-center">{{
              t('profile.no_posts', {name: profile?.name})
            }}</p>
          <div v-else class="text-gray text-center flex flex-col items-center justify-center">
            <IconEarning size="60px"/>
            <p class="max-w-96 py-3">Zaoferuj fanom płatną subskrypcję, aby otrzymać miesięczną zapłatę w zamian za
              dodatkowe korzyści
              premium.</p>
            <Button class="text-black" variant="outline" :to="{ path: '/creator-panel/subscription' }">Zacznij
              zarabiać
            </Button>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="supervision" :forceMount="true" v-if="activeTab === 'supervision'">
        <PostList :posts="subscriberPosts" :fetching="fetching"/>

        <div v-if="!fetching && Object.keys(subscriberPosts).length < 1"
             class="flex flex-grow items-center justify-center mt-5">
          <p v-if="!profile.is_owner" class="text-gray text-center">{{
              t('profile.no_posts', {name: profile?.name})
            }}</p>
          <div v-else class="text-gray text-center flex flex-col items-center justify-center">
            <IconEarning size="60px"/>
            <p class="max-w-96 py-3">Zaoferuj fanom płatną subskrypcję, aby otrzymać miesięczną zapłatę w zamian za
              dodatkowe korzyści
              premium.</p>
            <Button :to="{ path: '/creator-panel/subscription' }">Zacznij zarabiać</Button>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="purchased" :forceMount="true"
                   v-show="activeTab === 'purchased'">
        <PostList :posts="purchasedPosts" :fetching="fetchingPurchasedPosts"/>
      </TabsContent>
    </Tabs>

  </section>

  <NuxtPage :profile="profile" @update:profile="refreshProfile"/>

</template>
<style scoped>
.hidden-section {
  transform: translate(0, 100px);
  @media (min-width: 768px) {
    transform: translate(-50%, 100px);
  }

}

.slide-up {
  transform: translate(0, 0);
  @media (min-width: 768px) {
    transform: translate(-50%, 0);
  }
}
</style>